import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { BillingDocument } from '@/hooks/useBillingDocuments';
import { format } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import { FileText, Building2, Truck, Package, Calendar, Download, CheckCircle, Pencil, Check, X } from 'lucide-react';
import { useBillingDocuments } from '@/hooks/useBillingDocuments';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

interface BillingDocumentDialogProps {
  document: BillingDocument | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload: (document: BillingDocument) => void;
}

export const BillingDocumentDialog = ({ 
  document, 
  open, 
  onOpenChange,
  onDownload 
}: BillingDocumentDialogProps) => {
  const { updateDocument, isUpdating } = useBillingDocuments();
  const { t, i18n } = useTranslation();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(document?.title || '');

  // Synchroniser l'état quand le document change ou quand le dialog s'ouvre
  useEffect(() => {
    if (document && open) {
      setEditedTitle(document.title || '');
    }
  }, [document?.id, document?.title, open]);

  // Réinitialiser l'état d'édition quand le dialog se ferme
  useEffect(() => {
    if (!open) {
      setIsEditingTitle(false);
      if (document) {
        setEditedTitle(document.title || '');
      }
    }
  }, [open, document?.title]);

  if (!document) return null;

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      case 'de': return de;
      case 'it': return it;
      default: return enUS;
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      await updateDocument({
        id: document.id,
        status: 'paid',
        paid_at: new Date().toISOString(),
      });
      toast.success(t('billing.messages.markedAsPaid'));
    } catch (error) {
      console.error('Error marking as paid:', error);
    }
  };

  const handleSaveTitle = async () => {
    console.log('🔄 Tentative de sauvegarde du titre:', {
      documentId: document.id,
      oldTitle: document.title,
      newTitle: editedTitle.trim()
    });

    if (!editedTitle.trim()) {
      console.warn('⚠️ Titre vide, annulation');
      toast.error(t('billing.messages.titleRequired'));
      return;
    }
    
    try {
      const result = await updateDocument({
        id: document.id,
        title: editedTitle.trim(),
      });
      console.log('✅ Sauvegarde réussie:', result);
      toast.success(t('billing.messages.titleUpdated'));
      setIsEditingTitle(false);
    } catch (error) {
      console.error('❌ Erreur détaillée lors de la mise à jour:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCancelEdit = () => {
    setEditedTitle(document.title || '');
    setIsEditingTitle(false);
  };

  const getStatusBadge = () => {
    switch (document.status) {
      case 'paid':
        return <Badge className="bg-[#14A38B] text-white">{t('billing.status.paid')}</Badge>;
      case 'sent':
        return <Badge className="bg-[#0880AE] text-white">{t('billing.status.sent')}</Badge>;
      case 'cancelled':
        return <Badge className="bg-[#FF7171] text-white">{t('billing.status.cancelled')}</Badge>;
      default:
        return <Badge className="bg-[#87ADD4] text-white">{t('billing.status.draft')}</Badge>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-2xl font-semibold text-[#37699E]">
              {document.document_type === 'order_form' 
                ? t('billing.documentTypes.orderForm') 
                : t('billing.documentTypes.invoice')}
            </DialogTitle>
            {getStatusBadge()}
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Document Number - Non modifiable */}
          <div className="flex items-center gap-3 p-4 bg-[#EBF4F8] rounded-lg">
            <FileText className="text-[#0880AE]" size={24} />
            <div className="flex-1">
              <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                {t('billing.dialog.documentNumber')}
              </p>
              <p className="text-xl font-bold text-[#37699E]">
                {document.document_number}
              </p>
            </div>
          </div>

          {/* Custom Title - Modifiable (only for invoices) */}
          {document.document_type === 'invoice' && (
            !isEditingTitle ? (
              <div className="flex items-center gap-3 p-4 border-2 border-[#FFBF07] bg-[#FFBF07]/5 rounded-lg">
                <FileText className="text-[#FFBF07]" size={24} />
                <div className="flex-1">
                  <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                    {t('billing.dialog.customTitle')}
                  </p>
                  <p className="text-lg font-semibold text-[#2D3748]">
                    {document.title || t('billing.dialog.noTitle')}
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    setEditedTitle(document.title || '');
                    setIsEditingTitle(true);
                  }}
                  className="hover:bg-[#FFBF07]/10"
                >
                  <Pencil size={16} className="text-[#FFBF07]" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 border-2 border-[#FFBF07] bg-[#FFBF07]/5 rounded-lg">
                <FileText className="text-[#FFBF07]" size={24} />
                <div className="flex-1 space-y-2">
                  <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                    {t('billing.dialog.customTitle')}
                  </p>
                  <Input 
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    className="text-lg font-semibold border-[#FFBF07] focus:ring-[#FFBF07]"
                    placeholder={t('billing.dialog.titlePlaceholder')}
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleSaveTitle}
                    disabled={isUpdating}
                    className="hover:bg-[#14A38B]/10"
                  >
                    <Check size={16} className="text-[#14A38B]" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleCancelEdit}
                    className="hover:bg-[#FF7171]/10"
                  >
                    <X size={16} className="text-[#FF7171]" />
                  </Button>
                </div>
              </div>
            )
          )}

          {/* Parties */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 p-4 border border-[#DBE2EA] rounded-lg">
              <div className="flex items-center gap-2 text-[#37699E]">
                <Building2 size={18} />
                <h3 className="font-semibold">{t('billing.dialog.shipper')}</h3>
              </div>
              <p className="text-[#2D3748] font-medium">
                {document.shipper_companies?.name}
              </p>
              {document.shipper_companies?.email && (
                <p className="text-sm text-[#5A6B7A]">{document.shipper_companies.email}</p>
              )}
              {document.shipper_companies?.phone && (
                <p className="text-sm text-[#5A6B7A]">Tél: {document.shipper_companies.phone}</p>
              )}
              {document.shipper_companies?.address && (
                <p className="text-sm text-[#5A6B7A]">{document.shipper_companies.address}</p>
              )}
              {document.shipper_companies?.siret && (
                <p className="text-sm text-[#5A6B7A]">
                  ID fiscal: {document.shipper_companies.siret}
                </p>
              )}
            </div>

            <div className="space-y-2 p-4 border border-[#DBE2EA] rounded-lg">
              <div className="flex items-center gap-2 text-[#37699E]">
                <Truck size={18} />
                <h3 className="font-semibold">{t('billing.dialog.transporter')}</h3>
              </div>
              <p className="text-[#2D3748] font-medium">
                {document.transporter_companies?.name || 'N/A'}
              </p>
              {document.transporter_companies?.email && (
                <p className="text-sm text-[#5A6B7A]">{document.transporter_companies.email}</p>
              )}
              {document.transporter_companies?.phone && (
                <p className="text-sm text-[#5A6B7A]">Tél: {document.transporter_companies.phone}</p>
              )}
              {document.transporter_companies?.address && (
                <p className="text-sm text-[#5A6B7A]">
                  {document.transporter_companies.address}
                </p>
              )}
              {document.transporter_companies?.siret && (
                <p className="text-sm text-[#5A6B7A]">
                  ID fiscal: {document.transporter_companies.siret}
                </p>
              )}
            </div>
          </div>

          {/* Order Details */}
          <div className="space-y-3 p-4 border border-[#DBE2EA] rounded-lg">
            <div className="flex items-center gap-2 text-[#37699E]">
              <Package size={18} />
              <h3 className="font-semibold">{t('billing.dialog.orderDetails')}</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-[#5A6B7A]">{t('billing.dialog.orderNumber')}</span>
                <span className="text-sm font-medium text-[#2D3748]">
                  {document.orders?.order_number}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#5A6B7A]">{t('billing.dialog.cargo')}</span>
                <span className="text-sm font-medium text-[#2D3748]">
                  {document.orders?.cargo_title}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-[#5A6B7A]">{t('billing.dialog.route')}</span>
                <span className="text-sm font-medium text-[#2D3748]">
                  {document.orders?.pickup_city} → {document.orders?.delivery_city}
                </span>
              </div>
            </div>
          </div>

          <Separator />

          {/* Financial Details */}
          <div className="space-y-3">
            <h3 className="font-semibold text-[#37699E]">{t('billing.dialog.financialDetails')}</h3>
            <div className="space-y-2 p-4 bg-[#F8F9FA] rounded-lg">
              <div className="flex justify-between items-baseline">
                <span className="text-lg font-semibold text-[#37699E]">{t('billing.dialog.amountHT')}</span>
                <span className="text-2xl font-bold text-[#37699E]">
                  {document.amount.toFixed(2)} €
                </span>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[#37699E]">
              <Calendar size={18} />
              <h3 className="font-semibold">{t('billing.dialog.importantDates')}</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 border border-[#DBE2EA] rounded-lg">
                <p className="text-xs text-[#5A6B7A] uppercase tracking-wide mb-1">
                  {t('billing.dialog.issueDate')}
                </p>
                <p className="text-sm font-medium text-[#2D3748]">
                  {format(new Date(document.issued_at), 'dd MMMM yyyy', { locale: getDateLocale() })}
                </p>
              </div>
              {document.due_date && (
                <div className="p-3 border border-[#DBE2EA] rounded-lg">
                  <p className="text-xs text-[#5A6B7A] uppercase tracking-wide mb-1">
                    {t('billing.dialog.dueDate')}
                  </p>
                  <p className="text-sm font-medium text-[#2D3748]">
                    {format(new Date(document.due_date), 'dd MMMM yyyy', { locale: getDateLocale() })}
                  </p>
                </div>
              )}
              {document.paid_at && (
                <div className="p-3 border border-[#14A38B] bg-[#14A38B]/5 rounded-lg">
                  <p className="text-xs text-[#5A6B7A] uppercase tracking-wide mb-1">
                    {t('billing.dialog.paymentDate')}
                  </p>
                  <p className="text-sm font-medium text-[#14A38B]">
                    {format(new Date(document.paid_at), 'dd MMMM yyyy', { locale: getDateLocale() })}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {document.notes && (
            <div className="space-y-2 p-4 border border-[#DBE2EA] rounded-lg">
              <h3 className="font-semibold text-[#37699E]">{t('billing.dialog.notes')}</h3>
              <p className="text-sm text-[#2D3748]">{document.notes}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-[#DBE2EA]">
          {document.status !== 'paid' && document.document_type === 'invoice' && (
            <Button
              onClick={handleMarkAsPaid}
              disabled={isUpdating}
              className="bg-[#14A38B] hover:bg-[#10836F] text-white"
            >
              <CheckCircle size={16} className="mr-2" />
              {t('billing.dialog.markAsPaid')}
            </Button>
          )}
          <Button
            onClick={() => onDownload(document)}
            className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] ml-auto"
          >
            <Download size={16} className="mr-2" />
            {t('billing.dialog.downloadPDF')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
